import { FC } from 'react';
import { AdminAgentForm } from '@/types/adminAgent';

interface Props {
  form: AdminAgentForm;
  onChange: (patch: Partial<AdminAgentForm>) => void;
  /** Reportado pelo FormPage no submit pra avisar JSON inválido. */
  jsonError?: string | null;
}

function strOrEmpty(v: unknown): string {
  return typeof v === 'string' ? v : v != null ? String(v) : '';
}

function numOrEmpty(v: unknown): number | '' {
  return typeof v === 'number' ? v : '';
}

const SqlConfigTab: FC<Props> = ({ form, onChange, jsonError }) => {
  const cfg = form.sql_config || {};
  const tables = Array.isArray(cfg.allowed_tables) ? (cfg.allowed_tables as string[]) : [];

  const patch = (k: string, v: unknown) => {
    const next = { ...(form.sql_config || {}) } as Record<string, unknown>;
    next[k] = v;
    onChange({ sql_config: next });
  };

  return (
    <div className="space-y-5" data-testid="tab-sql">
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
        Configuração avançada — só altere se entende o impacto no Oráculo de
        SQL. Tabelas e timeouts mexem direto na execução de queries no banco.
      </div>

      <label className="block">
        <span className="text-sm font-semibold text-content-primary block mb-1">
          Banco de dados (connection_alias)
        </span>
        <select
          data-testid="sql-connection"
          value={strOrEmpty(cfg.connection_alias) || 'equiplex'}
          onChange={(e) => patch('connection_alias', e.target.value)}
          className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary"
        >
          <option value="equiplex">equiplex</option>
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-semibold text-content-primary block mb-1">
          Tabelas permitidas (1 por linha)
        </span>
        <textarea
          data-testid="sql-allowed-tables"
          rows={4}
          value={tables.join('\n')}
          onChange={(e) =>
            patch(
              'allowed_tables',
              e.target.value
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary font-mono text-sm"
        />
      </label>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">
            Max rows
          </span>
          <input
            type="number"
            data-testid="sql-max-rows"
            min={1}
            value={numOrEmpty(cfg.max_rows_returned)}
            onChange={(e) =>
              patch('max_rows_returned', e.target.value === '' ? null : Number(e.target.value))
            }
            className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary"
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">
            Timeout (s)
          </span>
          <input
            type="number"
            data-testid="sql-timeout"
            min={1}
            value={numOrEmpty(cfg.query_timeout_seconds)}
            onChange={(e) =>
              patch(
                'query_timeout_seconds',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
            className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary"
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">
            Iterações
          </span>
          <input
            type="number"
            data-testid="sql-iterations"
            min={1}
            max={10}
            value={numOrEmpty(cfg.max_agent_iterations)}
            onChange={(e) =>
              patch(
                'max_agent_iterations',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
            className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-semibold text-content-primary block mb-1">
          row_filters (JSON)
        </span>
        <textarea
          data-testid="sql-row-filters"
          rows={4}
          value={
            typeof cfg.row_filters === 'object' && cfg.row_filters !== null
              ? JSON.stringify(cfg.row_filters, null, 2)
              : strOrEmpty(cfg.row_filters)
          }
          onChange={(e) => patch('row_filters', e.target.value)}
          className={`w-full resize-none rounded-lg bg-surface-secondary border px-3 py-2 text-content-primary font-mono text-xs ${
            jsonError ? 'border-red-500/60' : 'border-surface-border'
          }`}
        />
        {jsonError && (
          <p className="text-xs text-red-400 mt-1" data-testid="sql-row-filters-error">
            {jsonError}
          </p>
        )}
      </label>
    </div>
  );
};

export default SqlConfigTab;
